<template>
  <BaseSimpleLayout class="shop-create">
    <template #title>販売店登録</template>
    <template v-if="loadingFlag" #main>
      <el-form
        ref="formRef"
        :model="createForm"
        :rules="rules"
        size="large"
        label-width="180px"
      >
        <div class="shop-master-create-form">
          <div>
            <el-form-item label="販売店名" prop="name">
              <el-input
                v-model="createForm.name"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item label="販売店コード" prop="shopCode">
              <el-input
                v-model="createForm.shopCode"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item
              label="販売店企業名"
              prop="companyId"
              class="shop-create-company"
            >
              <el-select
                v-model="createForm.companyId"
                placeholder="選択"
                filterable
                :disabled="isConfirm"
              >
                <el-option
                  v-for="company in companyList"
                  :key="company.id"
                  :label="company.name"
                  :value="company.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item class="shop-create-tel" label="電話番号" prop="tel">
              <el-input
                v-model="createForm.tel"
                autocomplete="off"
                maxlength="13"
                :disabled="isConfirm"
                @change="telRestrict()"
              />
            </el-form-item>
            <el-form-item class="shop-create-fax" label="FAX" prop="fax">
              <el-input
                v-model="createForm.fax"
                autocomplete="off"
                maxlength="12"
                :disabled="isConfirm"
                @change="faxRestrict()"
              />
            </el-form-item>
            <el-form-item
              class="shop-create-staff-name"
              label="メールアドレス"
              prop="email"
            >
              <el-input
                v-model="createForm.email"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item
              label="スマートブロッカー用ハーネスカット対応店"
              prop="isHarnessCut"
              class="shop-create-harness-cut"
            >
              <el-select
                v-model="createForm.isHarnessCut"
                filterable
                :disabled="isConfirm"
              >
                <el-option
                  v-for="isHarnessCutOption in isHarnessCutOptions"
                  :key="isHarnessCutOption.value"
                  :label="isHarnessCutOption.label"
                  :value="isHarnessCutOption.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item
              label="スマートブロッカー用Proモデル取扱店フラグ"
              prop="isProShop"
              class="shop-create-pro-shop"
            >
              <el-select
                v-model="createForm.isProShop"
                filterable
                :disabled="isConfirm"
              >
                <el-option
                  v-for="isProShopOption in isProShopOptions"
                  :key="isProShopOption.value"
                  :label="isProShopOption.label"
                  :value="isProShopOption.value"
                />
              </el-select>
            </el-form-item>
          </div>
          <div>
            <el-form-item label="郵便番号" prop="zip" class="shop-create-zip">
              <el-input
                v-model="createForm.zip"
                autocomplete="off"
                maxlength="7"
                :disabled="isConfirm"
                @change="zipRestrict()"
              />
            </el-form-item>
            <el-form-item
              label="都道府県"
              prop="prefecture"
              class="shop-create-pref"
            >
              <el-select
                v-model="createForm.prefecture"
                filterable
                placeholder="選択"
                :disabled="isConfirm"
              >
                <el-option
                  v-for="prefecture in prefectureList"
                  :key="prefecture"
                  :label="prefecture"
                  :value="prefecture"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="市区町村" prop="city" class="shop-create-city">
              <el-input
                v-model="createForm.city"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item label="住所" prop="address">
              <el-input
                v-model="createForm.address"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item label="住所サブ" prop="addressSub">
              <el-input
                v-model="createForm.addressSub"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
            <el-form-item
              class="shop-create-staff-name"
              label="担当者名"
              prop="staffName"
            >
              <el-input
                v-model="createForm.staffName"
                autocomplete="off"
                :disabled="isConfirm"
              />
            </el-form-item>
          </div>
        </div>
        <div v-if="isConfirm" class="button-area">
          <el-button size="large" type="info" @click="isConfirm = false">
            キャンセル
          </el-button>
          <el-button size="large" type="primary" @click="submit()">
            登録
          </el-button>
        </div>
        <div v-else class="button-area">
          <el-button
            size="large"
            type="info"
            @click="router.push('/master/shop')"
          >
            戻る
          </el-button>
          <el-button
            size="large"
            type="primary"
            @click="changeConfirm(formRef)"
          >
            確認
          </el-button>
        </div>
      </el-form>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useStore } from 'vuex';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import type { FormInstance, FormRules } from 'element-plus';
  import { ShopsSubmitData } from '@/types/master/shop';
  import { CompaniesDataState } from '@/types/master/company';
  import { prefectureList } from '@/libs/prefecture';
  import { ElLoading } from 'element-plus';
  import { telRestriction, zipRestriction } from '@/libs/common';

  const store = useStore();
  const formRef = ref<FormInstance>();
  const createForm = ref<ShopsSubmitData>({
    companyId: null,
    shopCode: '',
    name: '',
    zip: '',
    prefecture: '',
    city: '',
    address: '',
    addressSub: '',
    tel: '',
    fax: '',
    email: '',
    staffName: '',
    isHarnessCut: false,
    isProShop: false
  });
  const rules = ref<FormRules>({
    companyId: [{ required: true, message: '販売店企業名は必須です。' }],
    shopCode: [{ required: true, message: '販売店コードは必須です。' }],
    name: [{ required: true, message: '販売店名は必須です。' }],
    zip: [
      { required: true, message: '郵便番号は必須です。' },
      {
        min: 7,
        max: 7,
        message: '郵便番号は7桁の半角数字で入力してください。',
        trigger: 'blur'
      }
    ],
    prefecture: [{ required: true, message: '都道府県は必須です。' }],
    city: [{ required: true, message: '市区町村は必須です。' }],
    address: [{ required: true, message: '住所は必須です。' }],
    tel: [
      { required: true, message: '電話番号は必須です。' },
      {
        min: 10,
        max: 13,
        message:
          '電話番号は10桁から13桁の半角数字(ハイフン含む)で入力してください。',
        trigger: 'blur'
      }
    ],
    fax: [
      {
        min: 10,
        max: 12,
        message:
          'FAXは10桁から13桁の半角数字(ハイフン含む)で入力してください。',
        trigger: 'blur'
      }
    ],
    email: [
      {
        type: 'email',
        message: 'メールアドレス形式で入力して下さい。',
        trigger: 'blur'
      }
    ],
    staffName: [{ required: true, message: '担当者は必須です。' }],
    isHarnessCut: [{ required: true }],
    isProShop: [{ required: true }]
  });
  const companyList = ref<CompaniesDataState[]>([]);
  const loadingFlag = ref(false);
  const isConfirm = ref(false);
  const isHarnessCutOptions = ref<{ label: string; value: boolean }[]>([
    {
      label: '対応する',
      value: true
    },
    {
      label: '対応しない',
      value: false
    }
  ]);
  const isProShopOptions = ref<{ label: string; value: boolean }[]>([
    {
      label: '対応する',
      value: true
    },
    {
      label: '対応しない',
      value: false
    }
  ]);

  const initialize = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    await store.dispatch('companiesMaster/fetch');
    companyList.value = store.state.companiesMaster.listData;
    loadingInstance.close();
    loadingFlag.value = true;
  };
  const changeConfirm = async (formEl: FormInstance | undefined) => {
    if (!formEl) return;
    await formEl.validate((valid) => {
      if (!valid) {
        return false;
      }
      isConfirm.value = true;
    });
  };
  const submit = () => {
    store.dispatch('shopsMaster/submit', createForm.value);
  };
  const zipRestrict = () => {
    createForm.value.zip = zipRestriction(createForm.value.zip);
  };
  const telRestrict = () => {
    createForm.value.tel = telRestriction(createForm.value.tel);
  };
  const faxRestrict = () => {
    createForm.value.fax = telRestriction(createForm.value.fax);
  };
  initialize();
</script>
<style lang="scss">
  .shop-create {
    .shop-master-create-form {
      display: inline-flex;
    }
    form {
      width: 100%;
      display: inline-block;
    }

    .el-input {
      width: 300px;
      float: left;
    }

    .el-form-item {
      font-weight: 600 !important;
    }

    .shop-create-zip {
      .el-input {
        width: 90px;
        float: left;
      }
    }

    .shop-create-company {
      .el-input {
        width: 300px;
        float: left;
      }
      .el-select {
        width: 300px;
        float: left;
      }
    }
    .shop-create-zip {
      .el-input {
        width: 110px;
        float: left;
      }
    }

    .shop-create-pref {
      .el-input {
        width: 110px;
      }
      .el-select {
        width: 110px;
        float: left;
      }
    }

    .shop-create-city {
      .el-input {
        width: 150px;
        float: left;
      }
    }

    .shop-create-tel {
      .el-input {
        width: 135px;
        float: left;
      }
    }

    .shop-create-fax {
      .el-input {
        width: 135px;
        float: left;
      }
    }

    .shop-create-staff-name {
      .el-input {
        width: 200px;
        float: left;
      }
    }

    .shop-create-harness-cut,
    .shop-create-pro-shop {
      align-items: center;

      .el-form-item__label {
        line-height: 24px;
        white-space: normal;
        height: auto;
        padding-top: 2px;
      }

      .el-select {
        width: 300px;
        float: left;
      }
    }

    .shop-create-harness-cut {
      margin-top: 40px;
    }

    .button-area {
      margin: 10px 0px;
    }

    .el-button {
      font-weight: 600;
    }
  }
</style>
